import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  ThemeProvider,
  Typography,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  CheckCircleOutlineOutlined as CheckCircleOutlineIcon,
  ErrorOutlined as ErrorOutlineIcon,
  CancelOutlined as CancelOutlineIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import CaseStatusLayout from './CaseStatusLayout';
import caseStatusTheme, { CARD_PADDING } from '../../logic/components/CaseStatus/caseStatusTheme';
import { CaseStatusApi } from '../../logic/components/CaseStatus/api/CaseStatusApi';
import { PaymentResult } from '../../logic/components/CaseStatus/types/PaymentTypes';
import PaymentRow from '../../logic/components/CaseStatus/components/PaymentRow';
import CaseHeading from '../../logic/components/CaseStatus/components/CaseHeading';
import CaseStatusLanguageProvider, { useCaseStatusLanguage } from './CaseStatusLanguageProvider';
import { getPaymentReturnParams } from './paymentReturnParams';

const CasePaymentResultContent = () => {
  const theme = useTheme();
  const location = useLocation();
  const { t, fmtDate, translationsReady } = useCaseStatusLanguage();

  const { caseNumber, transactionId, responseCode, paymentItemType, paymentToken, providerType } =
    getPaymentReturnParams(location.search);

  const [isLoading, setIsLoading] = useState(true);
  const [paymentResult, setPaymentResult] = useState<PaymentResult | null>(null);
  const [error, setError] = useState<'cancelled' | 'error' | null>(null);

  useEffect(() => {
    if (responseCode.toLowerCase() === 'cancel') {
      setError('cancelled');
      setIsLoading(false);
      return;
    }

    if (!paymentToken) {
      setIsLoading(false);
      setError('error');
      return;
    }

    CaseStatusApi.getPaymentResult(
      caseNumber,
      transactionId,
      responseCode,
      paymentItemType,
      paymentToken,
      providerType
    )
      .then((result) => {
        if (result.cancelledByUser) {
          setError('cancelled');
        } else if (result.endsWithError) {
          setError('error');
        } else {
          setPaymentResult(result);
        }
      })
      .catch(() => setError('error'))
      .finally(() => {
        sessionStorage.removeItem(`paymentToken:${caseNumber}`);
        setIsLoading(false);
      });
  }, []);

  const handleBackToCase = () => {
    window.location.href = `/case-status?caseNumber=${encodeURIComponent(caseNumber)}`;
  };

  const heading = <CaseHeading caseNumber={caseNumber} />;

  const resultHero = (color: string, icon: React.ReactNode, title: string, body: string) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
        py: 3,
        px: CARD_PADDING,
        backgroundColor: `${color}14`,
        borderBottom: `1px solid ${color}30`,
        textAlign: 'center',
      }}
    >
      {icon}
      <Typography variant="h6" component="h2" sx={{ fontWeight: 700, color }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {body}
      </Typography>
    </Box>
  );

  const renderStatusScreen = (color: string, icon: React.ReactNode, title: string, body: string) => (
    <ThemeProvider theme={caseStatusTheme}>
      <CaseStatusLayout>
        <Container maxWidth="sm" sx={{ py: 3, px: 2 }}>
          {heading}
          <Paper elevation={1} sx={{ p: 0, overflow: 'hidden' }}>
            {resultHero(color, icon, title, body)}
            <Box sx={{ p: CARD_PADDING }}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                startIcon={<ArrowBackIcon />}
                onClick={handleBackToCase}
              >
                {t('BackToCaseStatus')}
              </Button>
            </Box>
          </Paper>
        </Container>
      </CaseStatusLayout>
    </ThemeProvider>
  );

  if (isLoading || !translationsReady) {
    return (
      <ThemeProvider theme={caseStatusTheme}>
        <CaseStatusLayout>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, py: 8 }}>
            <CircularProgress color="primary" />
          </Box>
        </CaseStatusLayout>
      </ThemeProvider>
    );
  }

  if (error === 'cancelled') {
    return renderStatusScreen(
      theme.palette.warning.main,
      <CancelOutlineIcon sx={{ fontSize: 40, color: theme.palette.warning.main }} />,
      t('PaymentCancelledTitle'),
      t('PaymentCancelledBody')
    );
  }

  if (error === 'error' || !paymentResult) {
    return renderStatusScreen(
      theme.palette.error.main,
      <ErrorOutlineIcon sx={{ fontSize: 40, color: theme.palette.error.main }} />,
      t('PaymentFailedTitle'),
      t('PaymentFailedBody')
    );
  }

  return (
    <ThemeProvider theme={caseStatusTheme}>
      <CaseStatusLayout>
        <Container maxWidth="sm" sx={{ py: 3, px: 2 }}>
          {heading}
          <Paper elevation={1} sx={{ p: 0, overflow: 'hidden' }}>
            {resultHero(
              theme.palette.success.main,
              <CheckCircleOutlineIcon sx={{ fontSize: 40, color: theme.palette.success.main }} />,
              t('PaymentCompleteTitle'),
              t('PaymentCompleteBody', caseNumber)
            )}

            <Box sx={{ p: CARD_PADDING }}>
              <Accordion
                disableGutters
                elevation={0}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  mb: 3,
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" color="text.secondary">
                    {t('PaymentDetails')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0, px: 1, pb: 1 }}>
                  <PaymentRow
                    label={t('TransactionDate')}
                    value={fmtDate(paymentResult.datePaid)}
                    copyValue={paymentResult.datePaid}
                  />
                  <PaymentRow
                    label={t('TransactionId')}
                    value={paymentResult.transactionId}
                    copyValue={paymentResult.transactionId}
                  />
                  {paymentResult.providerTransactionId && (
                    <PaymentRow
                      label={t('ProviderTransactionId')}
                      value={paymentResult.providerTransactionId}
                      copyValue={paymentResult.providerTransactionId}
                    />
                  )}
                  {paymentResult.caseRef && (
                    <PaymentRow
                      label={t('CaseRef')}
                      value={paymentResult.caseRef}
                      copyValue={paymentResult.caseRef}
                    />
                  )}
                </AccordionDetails>
              </Accordion>

              <Box sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  endIcon={<ArrowForwardIcon />}
                  onClick={handleBackToCase}
                >
                  {t('GoToCaseStatus')}
                </Button>
              </Box>
            </Box>
          </Paper>
        </Container>
      </CaseStatusLayout>
    </ThemeProvider>
  );
};

const CasePaymentResultRoute = () => {
  const location = useLocation();
  const { caseNumber, transactionId, paymentToken } = getPaymentReturnParams(location.search);

  const loadTranslations = useCallback(
    (culture: string) =>
      CaseStatusApi.getPaymentTranslations(culture, caseNumber, transactionId, paymentToken),
    [caseNumber, transactionId, paymentToken]
  );

  return (
    <CaseStatusLanguageProvider
      loadTranslations={loadTranslations}
    >
      <CasePaymentResultContent />
    </CaseStatusLanguageProvider>
  );
};

export default CasePaymentResultRoute;
